/**
 * AnnulmentNoticeModal
 * Carrier-facing popup shown when a placement has been annulled by admin.
 * Supports multi-notice navigation (currentIndex / total).
 */
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";

const DARK  = "#0A0A0A";
const RED   = "#EF4444";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

const REASON_LABELS = {
  "Non-Payment":              "Non-Payment",
  "Payment Arrears":          "Payment Arrears",
  "Fraud / Misrepresentation":"Fraud / Misrepresentation",
  "Contract Breach":          "Contract Breach",
  "Driver Withdrew":          "Driver Withdrew",
  "Other":                    "Other",
};

export function AnnulmentNoticeModal({
  notices = [],
  currentIndex = 0,
  total = 1,
  onPrev,
  onNext,
  onAcknowledge,
  loading = false,
}) {
  const notice = notices[currentIndex];
  if (!notice) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="annul-backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9900] flex items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
      >
        <motion.div
          key="annul-card"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
          className="w-full max-w-md"
          style={{ background: DARK, border: `1px solid rgba(239,68,68,0.35)` }}
        >
          {/* Multi-notice navigation */}
          {total > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5"
              style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
              <button onClick={onPrev} disabled={currentIndex === 0}
                className="p-1 disabled:opacity-25 hover:text-white transition-colors"
                style={{ color: "rgba(255,255,255,0.4)" }}>
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="font-head text-[9px] uppercase tracking-[0.25em]" style={{ color: "rgba(255,255,255,0.35)" }}>
                {currentIndex + 1} of {total} notice{total > 1 ? "s" : ""}
              </span>
              <button onClick={onNext} disabled={currentIndex === total - 1}
                className="p-1 disabled:opacity-25 hover:text-white transition-colors"
                style={{ color: "rgba(255,255,255,0.4)" }}>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Header */}
          <div className="px-6 pt-6 pb-5" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(239,68,68,0.04)" }}>
            <div className="flex items-center gap-3 mb-1">
              <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: RED }} />
              <p className="font-head text-base font-black uppercase tracking-[0.2em]" style={{ color: RED }}>
                Agreement Annulled
              </p>
            </div>
            <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.45)" }}>
              Hylas Companion has annulled the placement agreement below. Please review the details.
            </p>
          </div>

          {/* Notice body */}
          <div className="px-6 py-5 space-y-4">
            {/* Driver */}
            <div>
              <p className="font-head text-[9px] uppercase tracking-[0.2em] mb-1" style={{ color: "rgba(255,255,255,0.3)" }}>Driver</p>
              <p className="font-head text-sm font-bold text-white">{notice.driver_name || "—"}</p>
            </div>

            {/* Reason badge */}
            <div>
              <p className="font-head text-[9px] uppercase tracking-[0.2em] mb-1.5" style={{ color: "rgba(255,255,255,0.3)" }}>Reason</p>
              <span className="px-3 py-1 font-head text-xs font-black uppercase tracking-[0.15em]"
                style={{ background: "rgba(239,68,68,0.12)", color: RED, border: "1px solid rgba(239,68,68,0.3)" }}>
                {REASON_LABELS[notice.annulment_reason] || notice.annulment_reason || "—"}
              </span>
            </div>

            {/* Notes */}
            {notice.annulment_notes && (
              <div>
                <p className="font-head text-[9px] uppercase tracking-[0.2em] mb-1" style={{ color: "rgba(255,255,255,0.3)" }}>Notes from Hylas</p>
                <p className="text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>{notice.annulment_notes}</p>
              </div>
            )}

            {/* Date */}
            <div>
              <p className="font-head text-[9px] uppercase tracking-[0.2em] mb-0.5" style={{ color: "rgba(255,255,255,0.3)" }}>Date &amp; Time</p>
              <p className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>{formatDate(notice.created_at)}</p>
            </div>

            <p className="text-[10px] leading-relaxed" style={{ color: "rgba(255,255,255,0.3)" }}>
              For questions regarding this annulment, contact{" "}
              <a href="mailto:contact@hylascompanion.com" className="underline" style={{ color: "rgba(255,255,255,0.5)" }}>
                contact@hylascompanion.com
              </a>
            </p>
          </div>

          {/* Acknowledge */}
          <div className="px-6 pb-6">
            <button
              data-testid={`acknowledge-annulment-btn-${notice.notification_id}`}
              onClick={() => onAcknowledge(notice.notification_id)}
              disabled={loading}
              className="w-full py-3 font-head text-xs font-black uppercase tracking-[0.25em] transition-all hover:brightness-110 disabled:opacity-50"
              style={{ background: "rgba(239,68,68,0.12)", color: RED, border: `1px solid rgba(239,68,68,0.35)` }}>
              {loading ? "Acknowledging…" : "Acknowledge →"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
